import {
	DndContext,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ELEMENT_TYPES, ELEMENT_TYPE_COLORS } from "../constants/boqElementTypes";
import { BoQDropBeforeRow, BoQTableRow, DROP_BEFORE_PREFIX } from "./BoQDragDrop";
import "./BoQGrid.scss";
import { BoQDraftContext } from "./BoQGridContext";
import { BoQRowContextMenu } from "./BoQRowContextMenu";
import { BOQ_UNITS_SET, UNIT_OPTIONS, collectAllUnitTags } from "./UnitTagDropdown";
import { useBoQColumns } from "./useBoQColumns";

export { getDefaultBoQRow } from "./BoQGridContext";

/** For group/subgroup rows: first N columns (drag, expand, type-badge, OZ), then description colspan, then empty remainder. */
const GROUP_SUBGROUP_FIRST_COLUMNS = 4;
const GROUP_SUBGROUP_DESCRIPTION_COLSPAN = 3;
const DESCRIPTION_COLUMN_INDEX = 4;

/**
 * TanStack Table grid (tree): Drag column, Expand column, OZ, Beschreibung, ...
 * Selection controlled by selectedRowId. editingRowId shows inline edit for shortText and focuses it.
 * Optional onMoveRow(dragId, overId, placement) for drag-and-drop reorder.
 */
const BoQGrid = ({
	data = [],
	onRowChange,
	onSelectionChange,
	selectedRowId = null,
	editingRowId = null,
	expanded: expandedProp,
	onExpandedChange,
	onMoveRow,
	onCanDrop,
	onContextMenuAction,
	readOnly = false,
	/** Set<string> of row ids that have an OZ validation error. */
	ozErrorRowIds = null,
}) => {
	const { t } = useTranslation();
	const [expandedInternal, setExpandedInternal] = useState({});
	const expanded = expandedProp ?? expandedInternal;
	const setExpanded = onExpandedChange ?? setExpandedInternal;
	const editInputRef = useRef(null);

	// Tracks live-typed (unpersisted) values for the editing row so derived
	// columns (totalPrice, totalPriceGross) update on every keystroke without
	// waiting for blur-commit to flush to rows state.
	const [draftValues, setDraftValues] = useState({});
	useEffect(() => { setDraftValues({}); }, [editingRowId]);

	// Session-scoped accumulator for custom unit tags (never shrinks during the session).
	// Initialises from any custom tags already persisted in row data (handles page reload).
	const [customUnitTags, setCustomUnitTags] = useState(() => {
		const persisted = collectAllUnitTags(data).filter((tag) => !BOQ_UNITS_SET.has(tag));
		return new Set(persisted);
	});

	const unitOptionsWithCustom = useMemo(() => {
		if (customUnitTags.size === 0) return UNIT_OPTIONS;
		const customOptions = [...customUnitTags].map((tag) => ({ value: tag, label: tag }));
		return [...UNIT_OPTIONS, ...customOptions];
	}, [customUnitTags]);
	// Ref so column definitions can access the latest options without re-running their useMemo
	const unitOptionsRef = useRef(unitOptionsWithCustom);
	unitOptionsRef.current = unitOptionsWithCustom;

	// Intercepts unit tag changes: forwards to stableOnRowChange AND grows the accumulator
	const handleUnitTagChange = useCallback((rowId, newTag) => {
		if (newTag && !BOQ_UNITS_SET.has(newTag)) {
			setCustomUnitTags((prev) => {
				if (prev.has(newTag)) return prev;
				const next = new Set(prev);
				next.add(newTag);
				return next;
			});
		}
		onRowChangeRef.current?.(rowId, { unitTag: newTag });
	}, []);
	const handleUnitTagChangeRef = useRef(handleUnitTagChange);
	handleUnitTagChangeRef.current = handleUnitTagChange;

	// Ref so column definitions can access the latest OZ error set without re-running useMemo.
	const ozErrorRowIdsRef = useRef(ozErrorRowIds);
	ozErrorRowIdsRef.current = ozErrorRowIds;

	// Stabilize callbacks so columns useMemo doesn't recompute on every parent render
	const onRowChangeRef = useRef(onRowChange);
	onRowChangeRef.current = onRowChange;
	const stableOnRowChange = useCallback((...args) => onRowChangeRef.current?.(...args), []);

	const onSelectionChangeRef = useRef(onSelectionChange);
	onSelectionChangeRef.current = onSelectionChange;
	const stableOnSelectionChange = useCallback((...args) => onSelectionChangeRef.current?.(...args), []);

	const rowSelection = useMemo(
		() => (selectedRowId ? { [selectedRowId]: true } : {}),
		[selectedRowId],
	);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
	);

	const [dropIndicator, setDropIndicator] = useState(null);
	const [contextMenu, setContextMenu] = useState(null);
	const pointerRef = useRef({ x: 0, y: 0 });

	const onDragEndRef = useRef(onMoveRow);
	onDragEndRef.current = onMoveRow;
	const onContextMenuActionRef = useRef(onContextMenuAction);
	onContextMenuActionRef.current = onContextMenuAction;

	useEffect(() => {
		const onPointerMove = (e) => {
			pointerRef.current = { x: e.clientX, y: e.clientY };
		};
		document.addEventListener("pointermove", onPointerMove, { passive: true });
		return () => document.removeEventListener("pointermove", onPointerMove);
	}, []);

	// When readOnly (finalized), treat editing as inactive regardless of the prop.
	const effectiveEditingRowId = readOnly ? null : editingRowId;

	useEffect(() => {
		if (effectiveEditingRowId && editInputRef.current) {
			editInputRef.current.focus();
		}
	}, [effectiveEditingRowId]);

	// Auto-expand collapsed container rows when cursor lingers in the "inside" zone for 600 ms.
	const expandTimerRef = useRef(null);
	useEffect(() => {
		clearTimeout(expandTimerRef.current);
		if (dropIndicator?.placement === "inside" && dropIndicator.overId) {
			expandTimerRef.current = setTimeout(() => {
				setExpanded((prev) => ({ ...prev, [dropIndicator.overId]: true }));
			}, 600);
		}
		return () => clearTimeout(expandTimerRef.current);
	}, [dropIndicator?.placement, dropIndicator?.overId]); // eslint-disable-line react-hooks/exhaustive-deps

	// Placement logic:
	// - Container rows (GROUP / SUBGROUP): top 25% → "before" (reorder), rest → "inside" (nest child).
	// - Non-container rows: top half → "before", bottom half → "after".
	const getPlacementFromPointer = useCallback((overRect, rowType) => {
		if (!overRect?.height) return "after";
		const relY = pointerRef.current.y - overRect.top;
		const isContainer = rowType === ELEMENT_TYPES.GROUP || rowType === ELEMENT_TYPES.SUBGROUP;
		if (isContainer) {
			return relY < overRect.height * 0.25 ? "before" : "inside";
		}
		return relY < overRect.height / 2 ? "before" : "after";
	}, []);

	const handleDragStart = useCallback(() => { setDropIndicator(null); }, []);

	const handleRowContextMenu = useCallback((e, row) => {
		e.preventDefault();
		const parentRow = row.parent ?? null;
		setContextMenu({ x: e.clientX, y: e.clientY, row, parentRow });
	}, []);

	const closeContextMenu = useCallback(() => setContextMenu(null), []);

	const columns = useBoQColumns({
		t,
		stableOnRowChange,
		effectiveEditingRowId,
		readOnly,
		unitOptionsRef,
		handleUnitTagChangeRef,
		editInputRef,
		setDraftValues,
		ozErrorRowIdsRef,
	});

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getRowId: (row) => row.id ?? String(Math.random()),
		getSubRows: (row) => row.children ?? [],
		state: { rowSelection, expanded },
		onRowSelectionChange: () => { },
		onExpandedChange: setExpanded,
		enableRowSelection: true,
		enableMultiRowSelection: false,
	});

	const handleDragOver = useCallback(
		(event) => {
			if (!event.over) {
				setDropIndicator(null);
				return;
			}
			const overId = event.over.id;
			if (String(overId).startsWith(DROP_BEFORE_PREFIX)) {
				const targetRowId = String(overId).slice(DROP_BEFORE_PREFIX.length);
				if (targetRowId === event.active.id) { setDropIndicator(null); return; }
				if (onCanDrop && !onCanDrop(event.active.id, targetRowId, "before")) { setDropIndicator(null); return; }
				const rect = event.over.rect;
				setDropIndicator({
					overId: targetRowId,
					placement: "before",
					rect: rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null,
					isSlot: true,
				});
				return;
			}
			if (overId === event.active.id) { setDropIndicator(null); return; }
			const flatRows = table.getRowModel().rows;
			const overRow = flatRows.find((r) => r.id === overId);
			if (!overRow) { setDropIndicator(null); return; }
			const placement = getPlacementFromPointer(event.over.rect, overRow.original?.type);
			if (onCanDrop && !onCanDrop(event.active.id, overId, placement)) { setDropIndicator(null); return; }
			const rect = event.over.rect;
			setDropIndicator({
				overId,
				placement,
				rect: rect ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height } : null,
				isSlot: false,
			});
		},
		[table, getPlacementFromPointer, onCanDrop],
	);

	const handleDragEnd = useCallback(
		(event) => {
			setDropIndicator(null);
			const { active, over } = event;
			if (!over || active.id === over.id) return;
			const cb = onDragEndRef.current;
			if (!cb) return;
			let overId = over.id;
			let placement;
			if (String(overId).startsWith(DROP_BEFORE_PREFIX)) {
				overId = String(overId).slice(DROP_BEFORE_PREFIX.length);
				placement = "before";
			} else {
				const flatRows = table.getRowModel().rows;
				const overRow = flatRows.find((r) => r.id === overId);
				placement = getPlacementFromPointer(over.rect, overRow?.original?.type);
			}
			cb(active.id, overId, placement);
		},
		[table, getPlacementFromPointer],
	);

	const indicatorStyle =
		dropIndicator?.rect &&
			dropIndicator?.placement !== "inside" &&
			typeof dropIndicator.rect.top === "number" &&
			typeof dropIndicator.rect.left === "number"
			? (() => {
				const r = dropIndicator.rect;
				const lineY = dropIndicator.isSlot
					? r.top + r.height
					: dropIndicator.placement === "before" ? r.top : r.top + r.height;
				const barHeight = 4;
				return { top: lineY - barHeight / 2, left: r.left, width: Math.max(r.width ?? 0, 100), height: barHeight };
			})()
			: null;

	return (
		<BoQDraftContext.Provider value={{ values: draftValues, editingRowId: effectiveEditingRowId }}>
			{contextMenu && !readOnly && (
				<BoQRowContextMenu
					x={contextMenu.x}
					y={contextMenu.y}
					row={contextMenu.row}
					parentRow={contextMenu.parentRow}
					onClose={closeContextMenu}
					onAction={(rowId, elementType, placement) => {
						closeContextMenu();
						onContextMenuActionRef.current?.(rowId, elementType, placement);
					}}
					t={t}
				/>
			)}
			<div className="boq-grid-wrapper">
				{dropIndicator && indicatorStyle && (
					<div
						className="boq-grid-drop-indicator"
						style={{
							position: "fixed",
							top: indicatorStyle.top,
							left: indicatorStyle.left,
							width: indicatorStyle.width,
							height: indicatorStyle.height,
							pointerEvents: "none",
						}}
						aria-hidden
					/>
				)}
				<DndContext sensors={sensors} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
					<table className="boq-grid-table">
						<thead>
							{table.getHeaderGroups().map((headerGroup) => (
								<tr key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<th
											key={header.id}
											style={{
												width: header.column.columnDef.size ?? header.column.getSize?.(),
												minWidth: header.column.columnDef.minSize,
												textAlign: header.column.columnDef.meta?.align ?? "left",
											}}
										>
											{flexRender(header.column.columnDef.header, header.getContext())}
										</th>
									))}
								</tr>
							))}
						</thead>
						<tbody>
							{!data?.length ? (
								<tr>
									<td colSpan={columns.length} className="boq-grid-empty-cell">
										{t("boq.empty.subtitle")}
									</td>
								</tr>
							) : (
								<>
									{table.getRowModel().rows[0]?.id && (
										<BoQDropBeforeRow
											firstRowId={table.getRowModel().rows[0].id}
											colSpan={columns.length}
										/>
									)}
									{table.getRowModel().rows.map((row) => {
										const type = row.original?.type;
										const typeClass = type ? `boq-grid-row--${String(type).replace(/_/g, "-")}` : "";
										const typeColor = type ? ELEMENT_TYPE_COLORS[type] : null;
										const isGroupOrSubgroup = type === ELEMENT_TYPES.GROUP || type === ELEMENT_TYPES.SUBGROUP;
										const visibleCells = row.getVisibleCells();
										return (
											<BoQTableRow
												key={row.id}
												row={row}
												dropIndicator={dropIndicator}
												onCanDrop={onCanDrop}
												onClick={() => stableOnSelectionChange?.(row.id, row.original)}
												onContextMenu={readOnly ? undefined : (e) => handleRowContextMenu(e, row)}
												className={`${typeClass} ${row.getIsSelected() ? "boq-grid-row-selected" : ""} ${editingRowId === row.original.id ? "boq-grid-row--editing" : ""} ${dropIndicator?.placement === "inside" && dropIndicator.overId === row.original.id ? "boq-grid-row--drop-inside" : ""}`.trim()}
												role="button"
												tabIndex={0}
												onKeyDown={(e) => {
													if (e.target.closest("input, textarea, select, [contenteditable=\"true\"]")) return;
													if (e.key === "Enter" || e.key === " ") {
														e.preventDefault();
														stableOnSelectionChange?.(row.id, row.original);
													}
												}}
												style={{ "--boq-row-depth": row.depth, "--boq-row-type-color": typeColor || undefined }}
											>
												{isGroupOrSubgroup ? (
													<>
														{visibleCells.slice(0, GROUP_SUBGROUP_FIRST_COLUMNS).map((cell) => (
															<td
																key={cell.id}
																style={{
																	width: cell.column.columnDef.size ?? cell.column.getSize?.(),
																	minWidth: cell.column.columnDef.minSize,
																	textAlign: cell.column.columnDef.meta?.align ?? "left",
																}}
															>
																{flexRender(cell.column.columnDef.cell, cell.getContext())}
															</td>
														))}
														<td
															colSpan={GROUP_SUBGROUP_DESCRIPTION_COLSPAN}
															className="boq-grid-description-merged"
														>
															{flexRender(
																visibleCells[DESCRIPTION_COLUMN_INDEX].column.columnDef.cell,
																visibleCells[DESCRIPTION_COLUMN_INDEX].getContext(),
															)}
														</td>
														{visibleCells
															.slice(GROUP_SUBGROUP_FIRST_COLUMNS + GROUP_SUBGROUP_DESCRIPTION_COLSPAN)
															.map((cell) => {
																const showValue = cell.column.id === "totalPrice" || cell.column.id === "totalPriceGross";
																return (
																	<td
																		key={cell.id}
																		style={{
																			width: cell.column.columnDef.size ?? cell.column.getSize?.(),
																			minWidth: cell.column.columnDef.minSize,
																			textAlign: cell.column.columnDef.meta?.align ?? "left",
																		}}
																	>
																		{showValue ? flexRender(cell.column.columnDef.cell, cell.getContext()) : null}
																	</td>
																);
															})}
													</>
												) : (
													visibleCells.map((cell) => (
														<td
															key={cell.id}
															style={{
																width: cell.column.columnDef.size ?? cell.column.getSize?.(),
																minWidth: cell.column.columnDef.minSize,
																textAlign: cell.column.columnDef.meta?.align ?? "left",
															}}
														>
															{flexRender(cell.column.columnDef.cell, cell.getContext())}
														</td>
													))
												)}
											</BoQTableRow>
										);
									})}
								</>
							)}
						</tbody>
					</table>
				</DndContext>
			</div>
		</BoQDraftContext.Provider>
	);
};

export default BoQGrid;
